import React, { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { api, Group, Player } from "../api/client";
import { Credentials, generateParentCredentials, Parent, parentsApi } from "../api/parents";
import { useConfirm } from "../components/ConfirmDialog";
import PageLoader from "../components/PageLoader";
import "./ParentsPage.css";

type StatusFilter = "active" | "deleted" | "all";

type FormState = { name: string; phone: string; email: string; login: string; password: string };

const EMPTY_FORM: FormState = { name: "", phone: "", email: "", login: "", password: "" };

type Linking = { parentID: string; linkedIDs: string[] };

type LinkForm = { playerID: string; discount: string };

const EMPTY_LINK: LinkForm = { playerID: "", discount: "" };

type Revealed = Credentials & { title: string; parentID: string; offerLink: boolean };

const byName = <T extends { name: string }>(a: T, b: T) => a.name.localeCompare(b.name, "ru");

const PHONE_QUERY = /^[\d\s()+-]+$/;

function matches(p: Parent, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (q === "") return true;
  const texts = [p.name, p.login, p.email ?? "", ...(p.children ?? []).map((c) => c.name)];
  if (texts.some((t) => t.toLowerCase().includes(q))) return true;
  // Phones are stored in whatever format they were typed, so compare digits only.
  const digits = PHONE_QUERY.test(q) ? q.replace(/\D/g, "") : "";
  return digits !== "" && (p.phone ?? "").replace(/\D/g, "").includes(digits);
}

export default function ParentsPage() {
  const [parents, setParents] = useState<Parent[]>([]);
  const [players, setPlayers] = useState<Player[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<StatusFilter>("active");
  const [query, setQuery] = useState("");
  const [editingID, setEditingID] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [linking, setLinking] = useState<Linking | null>(null);
  const [linkForm, setLinkForm] = useState<LinkForm>(EMPTY_LINK);
  const [revealed, setRevealed] = useState<Revealed | null>(null);
  const [copied, setCopied] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const { confirm, dialog } = useConfirm();

  const load = useCallback(async () => {
    try {
      const [parentList, playerList, groupList] = await Promise.all([
        parentsApi.list(),
        api.listPlayers(),
        api.listGroups(),
      ]);
      setParents((parentList ?? []).slice().sort(byName));
      setPlayers((playerList ?? []).slice().sort(byName));
      setGroups(groupList ?? []);
    } catch (err: any) {
      console.error("Failed to load parents", err);
      setError(err?.message || "Не удалось загрузить родителей");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(timer);
  }, [copied]);

  const groupName = new Map(groups.map((g) => [g.id, g.name]));
  const takenLogins = new Set(parents.map((p) => p.login));
  const editing = editingID === null ? null : parents.find((p) => p.id === editingID) ?? null;
  const linkingParent = linking ? parents.find((p) => p.id === linking.parentID) ?? null : null;
  const revealedParent = revealed ? parents.find((p) => p.id === revealed.parentID) ?? null : null;

  const visible = parents.filter(
    (p) => (status === "all" || (status === "active") === p.is_active) && matches(p, query)
  );

  const groupLabel = (child: Player) => (child.group_id ? groupName.get(child.group_id) ?? "—" : "без группы");

  const run = async <T,>(action: () => Promise<T>, failure: string): Promise<T | null> => {
    setBusy(true);
    setError(null);
    try {
      return await action();
    } catch (err: any) {
      console.error("Parent action failed", err);
      setError(err?.message || failure);
      return null;
    } finally {
      setBusy(false);
      await load();
    }
  };

  const scrollToPanel = () => panelRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });

  const set = (changes: Partial<FormState>) => setForm((f) => ({ ...f, ...changes }));

  const stopEditing = () => {
    setEditingID(null);
    setForm(EMPTY_FORM);
  };

  const startEditing = (p: Parent) => {
    setError(null);
    setRevealed(null);
    setLinking(null);
    setEditingID(p.id);
    setForm({ ...EMPTY_FORM, name: p.name, phone: p.phone ?? "", email: p.email ?? "" });
    scrollToPanel();
  };

  const name = form.name.trim();
  const hasContact = form.phone.trim() !== "" || form.email.trim() !== "";
  const canSubmit =
    !busy &&
    name !== "" &&
    hasContact &&
    (editing !== null || (form.login.trim() !== "" && form.password.trim() !== ""));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    const phone = form.phone.trim();
    const email = form.email.trim();

    if (editing) {
      const saved = await run(() => parentsApi.update(editing.id, { name, phone, email }), "Не удалось сохранить родителя");
      if (saved) stopEditing();
      return;
    }

    const login = form.login.trim();
    const password = form.password.trim();
    if (takenLogins.has(login)) {
      setError(`Логин ${login} уже занят — поменяйте его`);
      return;
    }
    const created = await run(
      () => parentsApi.create({ name, login, password, phone: phone || null, email: email || null }),
      "Не удалось добавить родителя"
    );
    if (created) {
      setForm(EMPTY_FORM);
      setCopied(false);
      setRevealed({ title: "Родитель добавлен", login, password, parentID: created.id, offerLink: true });
    }
  };

  const resetCredentials = async (p: Parent) => {
    const confirmed = await confirm({
      title: `Сменить логин и пароль родителя «${p.name}»?`,
      body: (
        <>
          <p>
            Старые логин и пароль перестанут работать. Родитель выйдет из приложения на всех
            устройствах, и уведомления не будут приходить, пока он не войдёт с новыми. Уже открытое
            приложение может проработать ещё до 15 минут.
          </p>
          <p>Новые логин и пароль появятся на этой странице — скопируйте их и отправьте родителю.</p>
        </>
      ),
      confirmLabel: "Сменить логин и пароль",
      danger: true,
    });
    if (!confirmed) return;
    const credentials = generateParentCredentials(takenLogins);
    const updated = await run(() => parentsApi.resetCredentials(p.id, credentials), "Не удалось сменить логин и пароль");
    if (updated) {
      stopEditing();
      setLinking(null);
      setCopied(false);
      setRevealed({ ...credentials, title: `Новые логин и пароль: ${p.name}`, parentID: p.id, offerLink: false });
      scrollToPanel();
    }
  };

  const remove = async (p: Parent) => {
    const confirmed = await confirm({
      title: `Удалить родителя «${p.name}»?`,
      body: (
        <>
          <p>
            Родитель не сможет войти в приложение: сервер завершит его сессии и перестанет слать ему
            уведомления. Уже открытое приложение может проработать ещё до 15 минут.
          </p>
          <p>
            Привязки к детям удалятся, и родитель выйдет из родительских чатов групп. Если у ребёнка
            не останется другого активного родителя, его неоплаченные счета отменятся.
          </p>
          <p>
            Логин, контакты и история платежей сохранятся. Родителя можно восстановить в списке
            удалённых, но детей придётся привязать заново.
          </p>
        </>
      ),
      confirmLabel: "Удалить родителя",
      danger: true,
    });
    if (!confirmed) return;
    if (editingID === p.id) stopEditing();
    if (linking?.parentID === p.id) setLinking(null);
    if (revealed?.parentID === p.id) setRevealed(null);
    await run(() => parentsApi.remove(p.id), "Не удалось удалить родителя");
  };

  const restore = (p: Parent) => run(() => parentsApi.restore(p.id), "Не удалось восстановить родителя");

  const startLink = async (p: Parent) => {
    setBusy(true);
    setError(null);
    try {
      // Linking the same child twice fails on the server with a bare 500, and the
      // list may be stale (or came without children if the server failed to load
      // them), so ask what is linked right now.
      const current = await parentsApi.listChildren(p.id);
      const linkedIDs = [...(p.children ?? []), ...(current ?? [])].map((c) => c.id);
      setRevealed(null);
      stopEditing();
      setLinkForm(EMPTY_LINK);
      setLinking({ parentID: p.id, linkedIDs });
      scrollToPanel();
    } catch (err: any) {
      console.error("Failed to load parent's children", err);
      setError(err?.message || "Не удалось загрузить детей родителя");
    } finally {
      setBusy(false);
    }
  };

  const offered = linking ? players.filter((pl) => pl.is_active && !linking.linkedIDs.includes(pl.id)) : [];
  const discountText = linkForm.discount.trim();
  const discount = discountText === "" ? 0 : Number(discountText);
  const discountValid = Number.isFinite(discount) && discount >= 0 && discount <= 100;
  const canLink = !busy && linkForm.playerID !== "" && discountValid;

  const submitLink = async (e: FormEvent) => {
    e.preventDefault();
    if (!linking || !canLink) return;
    const linked = await run(
      () => parentsApi.linkChild(linking.parentID, linkForm.playerID, discount),
      "Не удалось привязать ребёнка"
    );
    if (linked) setLinking(null);
  };

  const unlink = async (p: Parent, child: Player) => {
    const confirmed = await confirm({
      title: `Отвязать «${child.name}» от «${p.name}»?`,
      body: (
        <>
          <p>
            Родитель перестанет видеть ребёнка в приложении. Если других детей в этой группе у него
            нет, сервер уберёт его из родительского чата группы.
          </p>
          <p>Неоплаченные счета за ребёнка не отменятся. Скидку при новой привязке нужно будет указать заново.</p>
        </>
      ),
      confirmLabel: "Отвязать",
      danger: true,
    });
    if (!confirmed) return;
    await run(() => parentsApi.unlinkChild(p.id, child.id), "Не удалось отвязать ребёнка");
  };

  const credentialsLines = revealed ? [`Логин: ${revealed.login}`, `Пароль: ${revealed.password}`] : [];

  const copyCredentials = async () => {
    try {
      await navigator.clipboard.writeText(credentialsLines.join("\n"));
      setCopied(true);
    } catch (err) {
      console.error("Failed to copy credentials", err);
      setError("Не удалось скопировать — выделите текст и скопируйте вручную");
    }
  };

  if (loading) return <PageLoader />;

  return (
    <div className="page page-wide">
      <h2>Родители</h2>
      <p className="page-description">
        Аккаунты родителей. Родитель входит в приложение по логину и паролю и видит там привязанных
        детей. Когда ребёнка привязывают или отвязывают, сервер сам добавляет родителя в родительский
        чат группы или убирает из него. Удалённого родителя можно восстановить, но детей придётся
        привязать заново.
      </p>

      {error && (
        <div className="error" role="alert">
          {error}
        </div>
      )}

      <div ref={panelRef} className="parents-panel">
        {revealed ? (
          <div className="mav-card cp-created">
            <div className="mav-card-title-row">
              <h3>{revealed.title}</h3>
            </div>
            <div className="cp-credentials-box">
              <p className="cp-credentials">
                {credentialsLines.map((line) => (
                  <span key={line}>{line}</span>
                ))}
              </p>
              <button
                type="button"
                className="cp-copy"
                onClick={copyCredentials}
                aria-label="Скопировать логин и пароль"
                title="Скопировать логин и пароль"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                  focusable="false"
                >
                  {copied ? (
                    <path d="M5 12.5l4.5 4.5L19 7.5" />
                  ) : (
                    <>
                      <rect x="9" y="9" width="11" height="11" rx="2" />
                      <path d="M5 15V6a2 2 0 0 1 2-2h9" />
                    </>
                  )}
                </svg>
              </button>
            </div>
            {copied ? (
              <span className="mav-status-ok cp-copied" role="status">
                Логин и пароль скопированы
              </span>
            ) : (
              <span className="mav-hint">Отправьте их родителю: после закрытия пароль здесь больше не покажется.</span>
            )}
            <div className="mav-actions">
              {revealed.offerLink && revealedParent && (
                <button type="button" className="mav-btn-primary" disabled={busy} onClick={() => startLink(revealedParent)}>
                  Привязать ребёнка
                </button>
              )}
              <button type="button" className="mav-btn-secondary" disabled={busy} onClick={() => setRevealed(null)}>
                Готово
              </button>
            </div>
          </div>
        ) : linking && linkingParent ? (
          <form className="mav-card cp-form" aria-label="Привязка ребёнка" onSubmit={submitLink}>
            <h3 className="coach-form-title">{`Привязать ребёнка: ${linkingParent.name}`}</h3>
            <p className="parents-form-note">Сервер сам добавит родителя в родительский чат группы ребёнка.</p>
            <div className="cp-field">
              <label className="cp-field">
                <span>Ребёнок</span>
                <select
                  className="mav-input"
                  value={linkForm.playerID}
                  disabled={busy || offered.length === 0}
                  onChange={(e) => setLinkForm((f) => ({ ...f, playerID: e.target.value }))}
                >
                  <option value="">{offered.length === 0 ? "Все активные дети уже привязаны" : "Выберите ребёнка"}</option>
                  {offered.map((pl) => (
                    <option key={pl.id} value={pl.id}>
                      {`${pl.name} — ${groupLabel(pl)}`}
                    </option>
                  ))}
                </select>
              </label>
              <span className="mav-hint">Только активные дети, ещё не привязанные к этому родителю</span>
            </div>
            <div className="cp-field">
              <label className="cp-field">
                <span>Скидка, %</span>
                <input
                  className={discountValid ? "mav-input" : "mav-input has-error"}
                  type="number"
                  min={0}
                  max={100}
                  step="any"
                  inputMode="decimal"
                  value={linkForm.discount}
                  disabled={busy}
                  onChange={(e) => setLinkForm((f) => ({ ...f, discount: e.target.value }))}
                />
              </label>
              <span className={discountValid ? "mav-hint" : "mav-hint is-error"}>От 0 до 100, можно не заполнять</span>
            </div>
            <div className="cp-actions mav-actions">
              <button type="submit" className="mav-btn-primary" disabled={!canLink}>
                Привязать
              </button>
              <button type="button" className="mav-btn-secondary" disabled={busy} onClick={() => setLinking(null)}>
                Отмена
              </button>
            </div>
          </form>
        ) : (
          <form className="mav-card cp-form" aria-label={editing ? "Изменение родителя" : "Новый родитель"} onSubmit={submit}>
            <h3 className="coach-form-title">{editing ? `Изменение: ${editing.name}` : "Новый родитель"}</h3>
            <label className="cp-field">
              <span>Имя</span>
              <input className="mav-input" value={form.name} maxLength={100} disabled={busy} onChange={(e) => set({ name: e.target.value })} />
            </label>
            <label className="cp-field">
              <span>Телефон</span>
              <input className="mav-input" type="tel" value={form.phone} maxLength={30} disabled={busy} onChange={(e) => set({ phone: e.target.value })} />
            </label>
            <label className="cp-field">
              <span>Почта</span>
              <input className="mav-input" type="email" value={form.email} maxLength={100} disabled={busy} onChange={(e) => set({ email: e.target.value })} />
            </label>
            <p className="mav-hint parents-form-note">Нужен телефон или почта — хотя бы что-то одно.</p>
            {editing ? (
              <p className="mav-hint parents-form-note">Логин и пароль меняются кнопкой «Сменить логин и пароль» в списке.</p>
            ) : (
              <>
                <label className="cp-field">
                  <span>Логин</span>
                  <input
                    className="mav-input"
                    value={form.login}
                    maxLength={100}
                    autoComplete="off"
                    spellCheck={false}
                    disabled={busy}
                    onChange={(e) => set({ login: e.target.value })}
                  />
                </label>
                <label className="cp-field">
                  <span>Пароль</span>
                  <input
                    className="mav-input"
                    value={form.password}
                    maxLength={100}
                    autoComplete="off"
                    spellCheck={false}
                    disabled={busy}
                    onChange={(e) => set({ password: e.target.value })}
                  />
                </label>
                <div className="parents-form-note">
                  <button type="button" className="mav-btn-secondary" disabled={busy} onClick={() => set(generateParentCredentials(takenLogins))}>
                    Придумать логин и пароль
                  </button>
                </div>
              </>
            )}
            <div className="cp-actions mav-actions">
              <button type="submit" className="mav-btn-primary" disabled={!canSubmit}>
                {editing ? "Сохранить" : "Добавить родителя"}
              </button>
              {editing && (
                <button type="button" className="mav-btn-secondary" disabled={busy} onClick={stopEditing}>
                  Отмена
                </button>
              )}
            </div>
          </form>
        )}
      </div>

      <div className="parents-filters">
        <label className="cp-field">
          <span>Статус</span>
          <select className="mav-input" value={status} onChange={(e) => setStatus(e.target.value as StatusFilter)}>
            <option value="active">Активные</option>
            <option value="deleted">Удалённые</option>
            <option value="all">Все</option>
          </select>
        </label>
        <label className="cp-field">
          <span>Поиск</span>
          <input
            className="mav-input"
            type="search"
            placeholder="Родитель, ребёнок, телефон или логин"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>
      </div>
      <p className="parents-count">
        Показано {visible.length} из {parents.length}
      </p>

      <div className="table-wrap">
        <table className="settings-table stack-table parents-table">
          <thead>
            <tr>
              <th>Родитель</th>
              <th>Дети</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 ? (
              <tr>
                <td colSpan={3} className="empty-cell">
                  {error ? "—" : parents.length === 0 ? "Родителей пока нет" : "Никого не нашлось"}
                </td>
              </tr>
            ) : (
              visible.map((p) => {
                const children = p.children ?? [];
                return (
                  <tr key={p.id}>
                    <td className="cell-parent">
                      <span className="parent-name">{p.name}</span>
                      <span className="parent-meta">{p.login}</span>
                      {p.phone && <span className="parent-meta">{p.phone}</span>}
                      {p.email && <span className="parent-meta">{p.email}</span>}
                      {!p.is_active && <span className="mav-badge mav-badge-muted">Удалён</span>}
                    </td>
                    <td className="cell-parent-children">
                      {children.length === 0 ? (
                        <span className="mav-status-muted">{p.is_active ? "Детей нет" : "—"}</span>
                      ) : (
                        <ul className="parent-children">
                          {children.map((c) => (
                            <li key={c.id}>
                              <span className="parent-child">
                                <span className="parent-child-name">{c.name}</span>
                                <span className="parent-meta">
                                  {groupLabel(c)}
                                  {!c.is_active && ", неактивен"}
                                </span>
                              </span>
                              {p.is_active && (
                                <button
                                  type="button"
                                  className="btn-quiet-danger parent-unlink"
                                  aria-label={`Отвязать ${c.name}`}
                                  disabled={busy}
                                  onClick={() => unlink(p, c)}
                                >
                                  Отвязать
                                </button>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                      {p.is_active && (
                        <button type="button" className="mav-btn-secondary parent-link" disabled={busy} onClick={() => startLink(p)}>
                          Привязать ребёнка
                        </button>
                      )}
                    </td>
                    <td className="cell-action">
                      <div className="row-actions">
                        {p.is_active ? (
                          <>
                            <button className="mav-btn-secondary" disabled={busy} onClick={() => resetCredentials(p)}>
                              Сменить логин и пароль
                            </button>
                            <button className="mav-btn-secondary" disabled={busy} onClick={() => startEditing(p)}>
                              Изменить
                            </button>
                            <button className="btn-quiet-danger" disabled={busy} onClick={() => remove(p)}>
                              Удалить
                            </button>
                          </>
                        ) : (
                          <button className="mav-btn-secondary" disabled={busy} onClick={() => restore(p)}>
                            Восстановить
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {dialog}
    </div>
  );
}
